import {
	Checkbox,
	styled,
	TableCell,
	TableHead,
	TableRow,
	TableSortLabel,
} from "@mui/material";
import { types } from "api";
import { Order } from "helpers/tableComparator";
import { useAuthStatus } from "helpers/useAuthStatus";
import React from "react";

const PREFIX = "ViewGroupsPageTableHead";

const classes = {
	root: `${PREFIX}-root`,
	visuallyHidden: `${PREFIX}-visuallyHidden`,
};

const StyledTableHead = styled(TableHead)(() => ({
	[`& .${classes.root}`]: {
		width: "100%",
	},

	[`& .${classes.visuallyHidden}`]: {
		border: 0,
		clip: "rect(0 0 0 0)",
		height: 1,
		margin: -1,
		overflow: "hidden",
		padding: 0,
		position: "absolute",
		top: 20,
		width: 1,
	},
}));

interface HeadCell {
	disablePadding: boolean;
	id: keyof types.UserBasic;
	label: string;
	numeric: boolean;
}

const headCells: HeadCell[] = [
	{
		id: "firstName",
		numeric: false,
		disablePadding: true,
		label: "First Name",
	},
	{ id: "lastName", numeric: false, disablePadding: true, label: "Last Name" },
	{ id: "email", numeric: false, disablePadding: true, label: "Email" },
];

export interface ViewGroupsPageTableHeadProps {
	numSelected: number;
	onRequestSort: (
		event: React.MouseEvent<unknown>,
		property: keyof types.UserBasic,
	) => void;
	onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
	order: Order;
	orderBy: string;
	rowCount: number;
}

export const ViewGroupsPageTableHead = ({
	onSelectAllClick,
	order,
	orderBy,
	numSelected,
	rowCount,
	onRequestSort,
}: ViewGroupsPageTableHeadProps): JSX.Element => {
	const { role } = useAuthStatus();
	const createSortHandler =
		(property: keyof types.UserBasic) => (event: React.MouseEvent<unknown>) => {
			onRequestSort(event, property);
		};

	return (
		<StyledTableHead>
			<TableRow>
				<TableCell padding="checkbox">
					<Checkbox
						indeterminate={numSelected > 0 && numSelected < rowCount}
						checked={rowCount > 0 && numSelected === rowCount}
						onChange={onSelectAllClick}
						inputProps={{ "aria-label": "select all accounts" }}
						data-testid="check-all"
					/>
				</TableCell>
				{headCells.map((headCell) => (
					<TableCell
						key={headCell.id}
						align="left"
						padding={headCell.disablePadding ? "none" : "normal"}
						sortDirection={orderBy === headCell.id ? order : false}
					>
						<TableSortLabel
							active={orderBy === headCell.id}
							direction={orderBy === headCell.id ? order : "asc"}
							onClick={createSortHandler(headCell.id)}
							data-testid="sort-label"
						>
							{headCell.label}
							{orderBy === headCell.id ? (
								<span className={classes.visuallyHidden}>
									{order === "desc" ? "sorted descending" : "sorted ascending"}
								</span>
							) : null}
						</TableSortLabel>
					</TableCell>
				))}
				{role !== "student" && <TableCell align="left">Action</TableCell>}
			</TableRow>
		</StyledTableHead>
	);
};
